import { ReactNode, useEffect, useRef, useState } from "react";
import { debounceTime, fromEvent } from "rxjs";

import { IconButton } from "@client:common/button";
import { Ripple } from "@client:common/effects/ripple";
import { Icon } from "@client:common/icon";

import "./Drawer.light.scss";
import "./Drawer.dark.scss";

interface Props {
  placement: "left" | "right";
  panelType?: "collapsible" | "non-collapsible";
  children: ReactNode | ReactNode[];
  className?: string;
  collapsed?: boolean;
  isMobile?: boolean;
  openRightDrawer?: boolean;
  toggleOpenRightDrawer?: any;
}

export function Drawer(props: Props) {
  const [isCollapsed, setIsCollapsed] = useState(props.collapsed);
  const panelWidthRef = useRef(0);
  const domElementRef = useRef<HTMLDivElement>(null);
  const className = [
    "drawer",
    isCollapsed ? "collapsed" : "",
    props.className ? props.className : "",
    `placement--${props.placement}`,
    props.isMobile ? "mobile" : "",
    props.isMobile && !props.openRightDrawer ? "hidden" : ""
  ].join(" ");

  useEffect(() => {
    const minWidth = 60;
    const subscription = fromEvent(document, "mouseover")
      .pipe(debounceTime(50))
      .subscribe({
        next: () => {
          if (domElementRef.current) {
            panelWidthRef.current =
              domElementRef.current.getBoundingClientRect().width - minWidth;
          }
        }
      });
    setTimeout(() => {
      if (domElementRef.current && panelWidthRef.current) {
        panelWidthRef.current =
          domElementRef.current.getBoundingClientRect().width - minWidth;
      }
    }, 1000);
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (props.isMobile) {
      setIsCollapsed(false);
    }
  }, [props.isMobile]);

  return (
    <div
      ref={domElementRef}
      className={className}
      style={{
        margin: !props.isMobile && isCollapsed
          ? props.placement === "left"
            ? `0 0 0 ${-panelWidthRef.current}px`
            : `0 ${-panelWidthRef.current}px 0 0`
          : 0
      }}
    >
      {props.panelType === "non-collapsible" ? (
        ""
      ) : (
        <IconButton
          className="drawer__toggle"
          style="basic"
          color="primary"
          onClick={props.isMobile ? props.toggleOpenRightDrawer : () => setIsCollapsed(!isCollapsed)}
        >
          <Ripple />
          {props.placement === "left" ? (
            <>
              {isCollapsed ? <Icon icon="arrow_forward" /> : <Icon icon="arrow_back" />}
              <span className="drawer__toggle-text">{isCollapsed ? "Show Panel" : "Hide Panel"}</span>
            </>
          ) : (
            <>
              {
                isCollapsed ?
                  <>
                    <Icon icon="arrow_downward" />
                    <span className="drawer__toggle-text">Show Panel</span>
                  </> :
                  <>
                    <span className="drawer__toggle-text">Hide Panel</span>
                    <Icon icon="arrow_forward" />
                  </>
              }
            </>
          )}
        </IconButton>
      )}
      <div className="drawer__body">{props.children}</div>
    </div>
  );
}
